import { useCallback, useState, useRef } from "react";
import { Upload, FileText, Image, File, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { DocumentCategory, useDocuments } from "@/contexts/DocumentContext";
import { useToast } from "@/hooks/use-toast";

interface FileDropZoneProps {
  category: DocumentCategory;
  linkedEntityId?: string;
  linkedEntityRef?: string;
  onUploadComplete?: () => void;
  compact?: boolean;
}

const FILE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  "application/pdf": FileText,
  "image/jpeg": Image,
  "image/png": Image,
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileDropZone({ category, linkedEntityId, linkedEntityRef, onUploadComplete, compact }: FileDropZoneProps) {
  const { uploadDocument } = useDocuments();
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    setPendingFiles((prev) => [...prev, ...arr]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = "";
  }, [addFiles]);

  const removePending = (idx: number) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);
    let success = 0;
    let failed = 0;
    for (const file of pendingFiles) {
      try {
        await uploadDocument(file, category, linkedEntityId, linkedEntityRef);
        success++;
      } catch (err: unknown) {
        failed++;
        toast({ title: "Upload failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
      }
    }
    setUploading(false);
    setPendingFiles([]);
    if (success > 0) {
      toast({ title: `${success} file${success > 1 ? "s" : ""} uploaded`, description: `Category: ${category}` });
      onUploadComplete?.();
    }
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "border-2 border-dashed rounded-xl transition-all duration-200 cursor-pointer flex flex-col items-center justify-center gap-2 text-center",
          compact ? "p-4" : "p-8",
          isDragging
            ? "border-primary bg-primary/5 scale-[1.01]"
            : "border-border hover:border-primary/50 hover:bg-muted/30"
        )}
      >
        <Upload className={cn("text-muted-foreground", compact ? "h-5 w-5" : "h-8 w-8")} />
        <div>
          <p className={cn("font-medium text-foreground", compact ? "text-xs" : "text-sm")}>
            Drag & drop files here
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            PDF, JPG, PNG, DOCX — Max 10MB
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.docx"
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          {pendingFiles.map((file, idx) => {
            const Icon = FILE_ICONS[file.type] || File;
            return (
              <div key={idx} className="flex items-center gap-3 px-3 py-2 bg-muted/40 rounded-lg border border-border">
                <Icon className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{file.name}</p>
                  <p className="text-[10px] text-muted-foreground">{formatSize(file.size)}</p>
                </div>
                <button onClick={() => removePending(idx)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full py-2 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : `Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? "s" : ""}`}
          </button>
        </div>
      )}
    </div>
  );
}
